/**
 * ToastProvider — легкий non-blocking notification UI без external deps.
 * (UX γ-essential, 2026-05-21).
 *
 * Призначення: відобразити повідомлення (наприклад 403 "Недостатньо прав")
 * з конкретним detail з backend response без блокуючого `alert()`. Toast
 * закривається користувачем (×) або автоматично через 5 секунд.
 *
 * НЕ підтримує:
 *   - Action-кнопки у toast'і («перемикач org» — TECHDEBT UX-γ-full)
 *   - Persistent stack >3 одночасних toast'ів — нові виштовхують старі
 *
 * API:
 *   const toast = useToast();
 *   toast.show({ type: 'error', title: 'Помилка', message: 'Деталі...' });
 *   toast.show('Просте повідомлення'); // type='info' за замовч.
 */
import {
  createContext, useCallback, useContext, useEffect, useRef, useState,
  type ReactNode,
} from 'react';

export type ToastType = 'info' | 'warning' | 'error' | 'success';

export interface ToastOptions {
  message: string;
  title?: string;
  type?: ToastType;
  /** ms до автозакриття, 0 = не закривати автоматично. Default 5000. */
  durationMs?: number;
}

type ShowFn = (opts: ToastOptions | string) => void;

const ToastCtx = createContext<ShowFn | null>(null);

export function useToast(): { show: ShowFn } {
  const ctx = useContext(ToastCtx);
  if (!ctx) {
    // Fallback на console.warn (наприклад у тестах без провайдера)
    return {
      show: (opts) => {
        const msg = typeof opts === 'string' ? opts : opts.message;
        console.warn('[Toast fallback]', msg);
      },
    };
  }
  return { show: ctx };
}

interface Item extends ToastOptions {
  id: number;
}

const MAX_VISIBLE = 3;

const COLORS: Record<ToastType, { bg: string; border: string; text: string; icon: string }> = {
  info: { bg: '#eff6ff', border: '#60a5fa', text: '#1e40af', icon: 'ℹ' },
  warning: { bg: '#fef3c7', border: '#fbbf24', text: '#92400e', icon: '⚠' },
  error: { bg: '#fee2e2', border: '#f87171', text: '#991b1b', icon: '✗' },
  success: { bg: '#d1fae5', border: '#34d399', text: '#065f46', icon: '✓' },
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Item[]>([]);
  const counterRef = useRef(0);

  const dismiss = useCallback((id: number) => {
    setItems((arr) => arr.filter((t) => t.id !== id));
  }, []);

  const show = useCallback<ShowFn>((opts) => {
    const normalized: ToastOptions =
      typeof opts === 'string' ? { message: opts } : opts;
    counterRef.current += 1;
    const id = counterRef.current;
    const item: Item = {
      id,
      type: 'info',
      durationMs: 5000,
      ...normalized,
    };
    setItems((arr) => {
      const next = [...arr, item];
      // Якщо понад MAX_VISIBLE — виштовхуємо найстаріший
      if (next.length > MAX_VISIBLE) {
        return next.slice(next.length - MAX_VISIBLE);
      }
      return next;
    });
  }, []);

  // Автозакриття для кожного toast'а з durationMs > 0.
  // useEffect монтує таймер при появі нового item у списку.
  useEffect(() => {
    if (items.length === 0) return;
    const timers = items
      .filter((t) => t.durationMs && t.durationMs > 0)
      .map((t) => window.setTimeout(() => dismiss(t.id), t.durationMs));
    return () => {
      timers.forEach((tid) => window.clearTimeout(tid));
    };
  }, [items, dismiss]);

  return (
    <ToastCtx.Provider value={show}>
      {children}
      {items.length > 0 && (
        <div
          aria-live="polite"
          style={{
            position: 'fixed',
            top: '1rem',
            right: '1rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem',
            zIndex: 10000,
            maxWidth: 480,
          }}
        >
          {items.map((t) => {
            const c = COLORS[t.type ?? 'info'];
            return (
              <div
                key={t.id}
                role="alert"
                style={{
                  background: c.bg,
                  border: `1px solid ${c.border}`,
                  color: c.text,
                  borderRadius: 6,
                  padding: '0.75rem 1rem',
                  boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr auto',
                  gap: '0.75rem',
                  alignItems: 'start',
                  fontSize: '0.9rem',
                }}
              >
                <span style={{ fontSize: '1.1rem', lineHeight: 1.2 }}>{c.icon}</span>
                <div>
                  {t.title && (
                    <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>
                      {t.title}
                    </div>
                  )}
                  <div style={{ whiteSpace: 'pre-line' }}>{t.message}</div>
                </div>
                <button
                  onClick={() => dismiss(t.id)}
                  aria-label="Закрити"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: c.text,
                    cursor: 'pointer',
                    fontSize: '1.1rem',
                    lineHeight: 1,
                    padding: 0,
                  }}
                >
                  ×
                </button>
              </div>
            );
          })}
        </div>
      )}
    </ToastCtx.Provider>
  );
}

/**
 * Bridge для імперативного виклику toast з НЕ-React коду (axios інтерцептори).
 * Встановлюється при mount'і ToastProvider, читається у `client.ts` (interceptor).
 */
let imperativeShow: ShowFn | null = null;

export function showToastImperative(opts: ToastOptions | string): void {
  if (imperativeShow) {
    imperativeShow(opts);
    return;
  }
  // Fallback — якщо провайдер не змонтовано (наприклад при ранніх errors).
  const msg = typeof opts === 'string' ? opts : opts.message;
  console.warn('[Toast imperative fallback]', msg);
}

/**
 * Sub-component що реєструє show-callback у глобальному bridge.
 * Монтується автоматично всередині ToastProvider — окремий компонент,
 * щоб мати доступ до useToast після того як Provider змонтував його
 * Context (без цього imperativeShow був би null).
 */
function ToastBridge(): null {
  const { show } = useToast();
  useEffect(() => {
    imperativeShow = show;
    return () => {
      if (imperativeShow === show) imperativeShow = null;
    };
  }, [show]);
  return null;
}

// Експортуємо також варіант провайдера з bridge для main.tsx mount'у.
export function ToastProviderWithBridge({ children }: { children: ReactNode }) {
  return (
    <ToastProvider>
      <ToastBridge />
      {children}
    </ToastProvider>
  );
}
